// Delivery form (legacy deliveries/DeliveryFormComponent.vue).
//
// - Create: only from a sale → GET sales/{id} (drafts are refused), then
//   POST sales/{id}/deliveries.
// - Edit: GET deliveries/{id} → PUT deliveries/{id} (multipart _method=PUT when
//   attachments are added). customer_id and items are always re-sent because
//   DeliveryRequest requires them on update too.

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';

import { adminApi, apiErrorMessage, apiValidationErrors, createRecord, updateRecord } from '../../api/client';
import type { CrudCustomFormProps } from '../../crud/types';
import { t } from '../../i18n';
import { asArray, asRecord, metaString, str, toNumber, type AnyRecord } from '../documents/format';
import { UI } from '../../brand/ui';

export const DELIVERY_STATUSES = ['preparing', 'delivering', 'delivered'] as const;

type Line = {
  key: string;
  id: string;
  code: string;
  name: string;
  item_id: string;
  sale_item_id: string;
  unit_id: string;
  quantity: number;
  maxQuantity: number;
  // Lines with variations / portions / modifiers keep the sold breakdown (read-only, like legacy).
  locked: boolean;
  detail: string[];
  selected: {
    modifiers: Array<{ id: unknown; selected: unknown; quantity: number }>;
    variations: Array<{ id: unknown; meta: unknown; quantity: number }>;
    portions: Array<{ id: unknown; quantity: number; choosables: Array<{ id: unknown; selected: unknown }> }>;
  };
};

// Map a sale item (create) or a delivery item (edit) to a form line.
function toLine(entry: AnyRecord, fromDelivery: boolean, index: number): Line {
  const item = asRecord(entry.item) ?? {};
  const modifiers = asArray(entry.modifier_options).map((option) => ({
    id: option.modifier_id ?? asRecord(option.pivot)?.modifier_id,
    selected: option.id,
    quantity: toNumber(asRecord(option.pivot)?.quantity)
  }));
  const variations = asArray(entry.variations).map((variation) => ({ id: variation.id, meta: variation.meta, quantity: toNumber(asRecord(variation.pivot)?.quantity) }));
  const portions = asArray(entry.portions).map((portion) => {
    const pivot = asRecord(portion.pivot) ?? {};
    return {
      id: portion.id,
      quantity: toNumber(pivot.quantity),
      choosables: asArray(pivot.choosables).map((choice) => ({ id: choice.id, selected: choice.item_id }))
    };
  });
  const detail = [
    ...asArray(entry.variations).map((variation) => `${metaString(variation.meta)} × ${toNumber(asRecord(variation.pivot)?.quantity)}`),
    ...asArray(entry.portions).map((portion) => `${t('doc.portion')}: ${portion.name === 'regular' ? t('doc.regular') : str(portion.name)} × ${toNumber(asRecord(portion.pivot)?.quantity)}`),
    ...asArray(entry.modifier_options).map((option) => `${str(asRecord(option.modifier)?.title)}: ${str(asRecord(option.item)?.name ?? option.name)} × ${toNumber(asRecord(option.pivot)?.quantity)}`)
  ];
  const quantity = toNumber(entry.quantity);
  return {
    key: `${str(entry.id)}-${index}`,
    id: fromDelivery ? str(entry.id) : '',
    code: str(item.code ?? entry.code),
    name: str(item.name ?? entry.name),
    item_id: str(entry.item_id ?? item.id),
    sale_item_id: fromDelivery ? str(entry.sale_item_id) : str(entry.id),
    unit_id: str(entry.unit_id),
    quantity,
    maxQuantity: quantity,
    locked: Boolean(variations.length || portions.length || modifiers.length),
    detail,
    selected: { modifiers, variations, portions }
  };
}

type FormState = {
  date: string;
  status: string;
  reference: string;
  driver: string;
  delivered_by: string;
  delivered_at: string;
  received_by: string;
  details: string;
  customer_id: string;
  extra_attributes: Record<string, unknown>;
};

const today = () => new Date().toISOString().slice(0, 10);

function toLocalInput(value: unknown) {
  const text = str(value);
  if (!text) return '';
  const date = new Date(text.includes('T') || text.includes('Z') ? text : text.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return text.replace(' ', 'T').slice(0, 16);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function DeliveryFormBody({
  saleId,
  deliveryId,
  onCancel,
  onSaved
}: {
  saleId?: string | number | null;
  deliveryId?: string | number | null;
  onCancel: () => void;
  onSaved: (record?: AnyRecord) => void;
}) {
  const editing = deliveryId != null && deliveryId !== '';
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);
  const [lines, setLines] = useState<Line[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [attributes, setAttributes] = useState<AnyRecord[]>([]);
  const [targetSaleId, setTargetSaleId] = useState<string>(saleId ? String(saleId) : '');
  const [form, setForm] = useState<FormState>({
    date: today(),
    status: 'preparing',
    reference: '',
    driver: '',
    delivered_by: '',
    delivered_at: '',
    received_by: '',
    details: '',
    customer_id: '',
    extra_attributes: {}
  });

  useEffect(() => {
    let active = true;
    setLoading(true);
    const load = editing ? adminApi.get(`deliveries/${deliveryId}`) : adminApi.get(`sales/${saleId}`);
    load
      .then((response) => {
        if (!active) return;
        const record = response.data as AnyRecord;
        if (!editing && toNumber(record.draft) === 1) {
          setBlocked(t('orderActions.deliveryDraftBlocked'));
          return;
        }
        if (editing) {
          setTargetSaleId(str(record.sale_id));
          setForm({
            date: str(record.date).slice(0, 10) || today(),
            status: str(record.status) || 'preparing',
            reference: str(record.reference),
            driver: str(record.driver),
            delivered_by: str(record.delivered_by),
            delivered_at: toLocalInput(record.delivered_at),
            received_by: str(record.received_by),
            details: str(record.details),
            customer_id: str(record.customer_id),
            extra_attributes: asRecord(record.extra_attributes) ?? {}
          });
          setAttributes(asArray(record.attributes));
        } else {
          setForm((current) => ({ ...current, customer_id: str(record.customer_id) }));
        }
        setLines(asArray(record.items).map((entry, index) => toLine(entry, editing, index)));
      })
      .catch((error) => active && setBlocked(apiErrorMessage(error, t('common.actionFailed'))))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [deliveryId, editing, saleId]);

  const delivered = form.status === 'delivered';
  const set = (key: keyof FormState) => (value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: [] }));
  };
  const err = (key: string) => errors[key]?.[0];

  const clientErrors = useMemo(() => {
    const next: Record<string, string> = {};
    if (!form.date) next.date = t('orderActions.required');
    if (delivered) {
      if (!form.delivered_at) next.delivered_at = t('orderActions.required');
      if (!form.delivered_by.trim()) next.delivered_by = t('orderActions.required');
      if (!form.received_by.trim()) next.received_by = t('orderActions.required');
    }
    if (!lines.some((line) => line.quantity > 0)) next.items = t('orderActions.deliveryItemsRequired');
    return next;
  }, [delivered, form, lines]);
  const [showClientErrors, setShowClientErrors] = useState(false);
  const fieldError = (key: string) => err(key) ?? (showClientErrors ? clientErrors[key] : undefined);

  const submit = async () => {
    if (Object.keys(clientErrors).length) {
      setShowClientErrors(true);
      return;
    }
    setSaving(true);
    setNotice(null);
    setErrors({});
    const payload: Record<string, unknown> = {
      date: form.date,
      status: form.status,
      customer_id: form.customer_id,
      items: lines
        .filter((line) => line.quantity > 0)
        .map((line) => ({
          id: line.id,
          code: line.code,
          name: line.name,
          item_id: line.item_id || undefined,
          unit_id: line.unit_id || undefined,
          sale_item_id: line.sale_item_id || undefined,
          quantity: line.quantity,
          selected: line.selected
        }))
    };
    (['reference', 'driver', 'delivered_by', 'received_by', 'details'] as const).forEach((key) => {
      if (form[key].trim()) payload[key] = form[key].trim();
    });
    if (form.delivered_at) payload.delivered_at = `${form.delivered_at.replace('T', ' ')}:00`;
    if (attributes.length) payload.extra_attributes = form.extra_attributes;
    if (files.length) payload.attachments = files;
    try {
      const response = editing ? await updateRecord<AnyRecord>('deliveries', deliveryId as string, payload) : await createRecord<AnyRecord>(`sales/${targetSaleId}/deliveries`, payload);
      if (response && response.success === false) {
        setNotice(response.message ?? t('common.saveFailed'));
        return;
      }
      onSaved(response?.data);
    } catch (error) {
      const validation = apiValidationErrors(error);
      setErrors(validation);
      setNotice(Object.keys(validation).length ? Object.values(validation).flat()[0] ?? t('common.fixHighlighted') : apiErrorMessage(error, t('common.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  if (blocked)
    return (
      <Stack spacing={2}>
        <Alert severity="warning">{blocked}</Alert>
        <Box>
          <Button onClick={onCancel}>{t('action.close')}</Button>
        </Box>
      </Stack>
    );

  const text = (key: keyof FormState, label: string, extra: { required?: boolean; type?: string; multiline?: boolean } = {}) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      type={extra.type ?? 'text'}
      required={extra.required}
      multiline={extra.multiline}
      minRows={extra.multiline ? 3 : undefined}
      value={form[key] as string}
      onChange={(event) => set(key)(event.target.value)}
      error={Boolean(fieldError(key))}
      helperText={fieldError(key)}
      InputLabelProps={extra.type === 'date' || extra.type === 'datetime-local' ? { shrink: true } : undefined}
    />
  );

  return (
    <Stack spacing={2.5}>
      {notice ? <Alert severity="error">{notice}</Alert> : null}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>{text('date', t('doc.date'), { required: true, type: 'date' })}</Grid>
        <Grid size={{ xs: 12, md: 6 }}>{text('reference', t('doc.reference'))}</Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth size="small" required>
            <InputLabel>{t('doc.status')}</InputLabel>
            <Select label={t('doc.status')} value={form.status} onChange={(event) => set('status')(String(event.target.value))}>
              {DELIVERY_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {t(`orderActions.deliveryStatus.${status}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>{text('driver', t('doc.driver'))}</Grid>
        <Grid size={{ xs: 12, md: 6 }}>{text('delivered_by', t('doc.deliveredBy'), { required: delivered })}</Grid>
        <Grid size={{ xs: 12, md: 6 }}>{text('received_by', t('doc.receivedBy'), { required: delivered })}</Grid>
        <Grid size={{ xs: 12, md: 6 }}>{text('delivered_at', t('doc.deliveredAt'), { required: delivered, type: 'datetime-local' })}</Grid>
      </Grid>

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          {t('orderActions.deliveryItems')}
        </Typography>
        {fieldError('items') ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {fieldError('items')}
          </Alert>
        ) : null}
        {lines.length ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>{t('doc.description')}</TableCell>
                <TableCell align="right" sx={{ width: 140 }}>
                  {t('doc.quantity')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, index) => (
                <TableRow key={line.key}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <strong>{line.name}</strong> <span style={{ color: UI.textSecondary }}>[{line.code}]</span>
                    {line.detail.map((entry) => (
                      <div key={entry} style={{ fontSize: 12, color: UI.textSecondary }}>
                        {entry}
                      </div>
                    ))}
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={line.quantity}
                      disabled={line.locked}
                      inputProps={{ min: 0, max: line.maxQuantity, step: 'any', style: { textAlign: 'right' } }}
                      helperText={line.locked ? undefined : `${t('orderActions.max')} ${line.maxQuantity}`}
                      onChange={(event) => {
                        const next = Math.max(0, Math.min(line.maxQuantity, Number(event.target.value)));
                        setLines((current) => current.map((entry) => (entry.key === line.key ? { ...entry, quantity: Number.isFinite(next) ? next : 0 } : entry)));
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Alert severity="info">{t('orderActions.deliveryNoItems')}</Alert>
        )}
      </Box>

      {attributes.length ? (
        <Grid container spacing={2}>
          {attributes.map((attribute) => {
            const slug = str(attribute.slug);
            return (
              <Grid key={slug} size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={str(attribute.name)}
                  value={str(form.extra_attributes[slug])}
                  onChange={(event) => setForm((current) => ({ ...current, extra_attributes: { ...current.extra_attributes, [slug]: event.target.value } }))}
                />
              </Grid>
            );
          })}
        </Grid>
      ) : null}

      <Box>
        <Button component="label" variant="outlined" size="small">
          {t('action.chooseFiles')}
          <input hidden multiple type="file" onChange={(event) => setFiles(Array.from(event.target.files ?? []))} />
        </Button>
        {files.length ? (
          <Typography variant="caption" sx={{ ml: 1 }}>
            {files.map((file) => file.name).join(', ')}
          </Typography>
        ) : null}
      </Box>
      {text('details', t('orderActions.details'), { multiline: true })}

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onCancel} disabled={saving}>
          {t('action.cancel')}
        </Button>
        <Button variant="contained" onClick={() => void submit()} disabled={saving}>
          {saving ? t('orderActions.saving') : t('action.save')}
        </Button>
      </Stack>
    </Stack>
  );
}

// customForm for the deliveries resource (edit only — create goes through a sale).
export function DeliveryCustomForm({ recordId, prefill, onClose, onSaved }: CrudCustomFormProps) {
  const saleId = (prefill?.sale_id as string | undefined) ?? null;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700 }}>{recordId != null ? t('orderActions.editDelivery') : t('orderActions.addDelivery')}</DialogTitle>
      <DialogContent dividers>
        {recordId == null && !saleId ? (
          <Alert severity="info">{t('orderActions.deliveryFromSaleOnly')}</Alert>
        ) : (
          <DeliveryFormBody deliveryId={recordId} saleId={saleId} onCancel={onClose} onSaved={(record) => onSaved(record as never)} />
        )}
      </DialogContent>
      {recordId == null && !saleId ? (
        <DialogActions>
          <Button onClick={onClose}>{t('action.close')}</Button>
        </DialogActions>
      ) : null}
    </Dialog>
  );
}
